import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { doctors } from '@/mocks/homeData';

export default function DoctorsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="relative bg-background-100 py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="text-center max-w-[600px] mx-auto mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs tracking-[0.2em] uppercase text-primary-400 font-label"
          >
            Equipe
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-950 mt-4 leading-[1.1]"
          >
            Nossas<br />
            <span className="italic font-light">Especialistas.</span>
          </motion.h2>
        </div>

        {/* Doctor Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 max-w-[1100px] mx-auto">
          {doctors.map((doctor, index) => (
            <motion.div
              key={doctor.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 + index * 0.15, ease: [0.25, 0.1, 0.25, 1] }}
              className="group"
            >
              {/* Image — enhanced hover */}
              <Link
                to={`/${doctor.slug}`}
                className="block relative overflow-hidden rounded-lg aspect-[3/4] mb-6"
              >
                <img
                  src={doctor.image}
                  alt={doctor.name}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.04]"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                {/* Reveal text */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-6 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-75">
                  <span className="inline-flex items-center gap-2 text-white text-xs tracking-[0.08em] uppercase font-label whitespace-nowrap">
                    Ver perfil completo
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                  </span>
                </div>
                {/* Shine sweep */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                  <div className="absolute top-0 left-[-100%] w-[60%] h-full bg-gradient-to-r from-transparent via-white/8 to-transparent skew-x-[-12deg] group-hover:left-[120%] transition-all duration-900" />
                </div>
              </Link>

              {/* Info */}
              <div>
                <Link to={`/${doctor.slug}`}>
                  <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 hover:text-primary-500 transition-colors duration-300 whitespace-nowrap">
                    {doctor.name}
                  </h3>
                </Link>
                <p className="text-xs text-primary-500 tracking-[0.08em] uppercase mt-1 font-label whitespace-nowrap">
                  {doctor.role}
                </p>
                <p className="text-xs text-foreground-400 mt-2 font-label whitespace-nowrap">
                  {doctor.crm}
                </p>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  {doctor.shortBio}
                </p>

                {/* Specialties */}
                <div className="flex flex-wrap gap-2 mt-5">
                  {doctor.specialties.map((spec) => (
                    <motion.span
                      key={spec}
                      whileHover={{ scale: 1.04 }}
                      className="px-3 py-1.5 rounded-full bg-background-200 text-foreground-600 text-xs whitespace-nowrap font-label transition-colors duration-300 hover:bg-primary-50 hover:text-primary-600"
                    >
                      {spec}
                    </motion.span>
                  ))}
                </div>

                {/* Social */}
                <a
                  href={doctor.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-5 text-xs text-foreground-500 hover:text-primary-500 transition-all duration-200 font-label whitespace-nowrap group/social"
                >
                  <i className="ri-instagram-line transition-transform duration-300 group-hover/social:scale-110"></i>
                  Instagram
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}